import { Visibility } from '@teachur/http-client';
import type { DegreePlan, Degree, User } from '@teachur/http-client';
import { isUserSubscribed } from '@helpers';

export const MASTER_LOCKED_MESSAGE =
  "Your master's degree can't be changed. Please contact faculty support.";
export const MASTER_NOT_ON_SUBSCRIPTION_MESSAGE =
  "You can't switch to a master's degree on your current subscription. Please contact faculty support.";
export const MASTER_SUBSCRIPTION_ONLY_MESSAGE =
  "Your master's subscription only covers a master's degree. Please contact faculty support.";

const MASTER = 'master';
const ENROLLED_DEGREE_PRIORITY = ['master', 'bachelor', 'associate'];

type DegreeRef = Pick<Degree, 'id' | 'type'>;

export const enrolledDegreeIds = (user: User | undefined): number[] =>
  (user?.enrolledDegrees ?? []).map((enrolled) => enrolled.degreeId);

export const selectableDegrees = (degrees: Degree[], user: User | undefined): Degree[] => {
  const enrolled = new Set(enrolledDegreeIds(user));
  return degrees.filter(
    (degree) => degree.visibility === Visibility.Public || enrolled.has(degree.id)
  );
};

export const degreeChoiceBlockReason = (
  degree: DegreeRef,
  user: User | undefined
): string | null => {
  if (!user || !isUserSubscribed(user)) {
    return null;
  }
  const enrolledMaster = (user.enrolledDegrees ?? []).find(
    (enrolled) => enrolled.degree?.type === MASTER
  );
  if (enrolledMaster && enrolledMaster.degreeId !== degree.id) {
    return MASTER_LOCKED_MESSAGE;
  }
  const onMasterSubscription = user.subscriptionType === MASTER;
  if (degree.type === MASTER && !onMasterSubscription) {
    return MASTER_NOT_ON_SUBSCRIPTION_MESSAGE;
  }
  if (degree.type !== MASTER && onMasterSubscription) {
    return MASTER_SUBSCRIPTION_ONLY_MESSAGE;
  }
  return null;
};

export interface DegreeTypeChoice {
  type: string;
  label: string;
  degrees: Degree[];
  blockReason: string | null;
}

const DEGREE_TYPES: { type: string; label: string }[] = [
  { type: 'associate', label: 'Associate' },
  { type: 'bachelor', label: "Bachelor's" },
  { type: 'master', label: "Master's" },
];

export const degreeTypeChoices = (
  degrees: Degree[],
  user: User | undefined
): DegreeTypeChoice[] => {
  const options = selectableDegrees(degrees, user);
  return DEGREE_TYPES.map(({ type, label }) => {
    const ofType = options.filter((degree) => degree.type === type);
    const allowed = ofType.filter((degree) => !degreeChoiceBlockReason(degree, user));
    const blockReason =
      allowed.length === 0 && ofType.length > 0 ? degreeChoiceBlockReason(ofType[0], user) : null;
    return { type, label, degrees: allowed, blockReason };
  }).filter((choice) => choice.degrees.length > 0 || choice.blockReason);
};

export const preselectedDegreeId = (
  savedPlans: DegreePlan[],
  user: User | undefined,
  targetDegreeId: number | null | undefined
): number | null => {
  const planned = savedPlans[savedPlans.length - 1]?.degree.id;
  if (planned != null) {
    return planned;
  }
  const enrolled = user?.enrolledDegrees ?? [];
  for (const type of ENROLLED_DEGREE_PRIORITY) {
    const match = enrolled.find((userDegree) => userDegree.degree?.type === type);
    if (match) {
      return match.degreeId;
    }
  }
  return targetDegreeId ?? null;
};
